import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMemo, useState } from "react";
import { AdminCard, SectionHeader, StatusPill, EmptyState, btnPrimary, btnGhost } from "@/lib/admin/ui";
import { Users, Search, Filter, Plus, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";

export const Route = createFileRoute("/admin/clients")({
  component: ClientsList,
});

const PLAN_FILTERS = ["all", "landing", "flex", "premium"];
const STATUS_FILTERS = ["all", "in_progress", "review", "delivered", "delayed", "urgent", "archived"];

function ClientsList() {
  const [query, setQuery] = useState("");
  const [plan, setPlan] = useState("all");
  const [status, setStatus] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-clients"],
    queryFn: async () => {
      const [projects, profiles, invoices, tickets] = await Promise.all([
        supabase.from("projects").select("id, owner_id, name, plan, kanban_status, status, project_manager, website_url, delivery_date, progress, created_at").order("created_at", { ascending: false }),
        supabase.from("profiles").select("id, full_name, company, phone, email, contact_person"),
        supabase.from("invoices").select("project_id, amount, status"),
        supabase.from("tickets").select("project_id, status"),
      ]);
      return {
        projects: projects.data ?? [],
        profiles: Object.fromEntries((profiles.data ?? []).map((p) => [p.id, p])),
        invoicesByProject: groupBy(invoices.data ?? [], "project_id"),
        ticketsByProject: groupBy(tickets.data ?? [], "project_id"),
      };
    },
  });

  const filtered = useMemo(() => {
    return (data?.projects ?? []).filter((p) => {
      if (plan !== "all" && p.plan?.toLowerCase() !== plan) return false;
      if (status !== "all") {
        const s = (p.kanban_status ?? p.status ?? "").toLowerCase();
        if (status === "urgent" && s !== "urgent") return false;
        if (status !== "urgent" && !s.includes(status)) return false;
      }
      if (query) {
        const prof = data?.profiles[p.owner_id];
        const hay = `${p.name} ${prof?.full_name ?? ""} ${prof?.company ?? ""}`.toLowerCase();
        if (!hay.includes(query.toLowerCase())) return false;
      }
      return true;
    });
  }, [data, query, plan, status]);

  return (
    <div className="max-w-[1600px] space-y-6">
      <SectionHeader eyebrow="CRM" title="Clients" description={`${filtered.length} clienti · gestione completa`}
        actions={<button className={btnPrimary}><Plus className="h-3.5 w-3.5" /> Nuovo cliente</button>} />

      {/* Filters */}
      <AdminCard className="p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca nome, azienda, progetto…"
            className="w-full pl-9 pr-3 h-9 rounded-lg bg-white/[0.03] border border-white/10 text-sm outline-none focus:border-white/25" />
        </div>
        <Filter className="h-3.5 w-3.5 text-white/40 ml-1" />
        <FilterGroup label="Piano" value={plan} onChange={setPlan} options={PLAN_FILTERS} />
        <FilterGroup label="Stato" value={status} onChange={setStatus} options={STATUS_FILTERS} />
      </AdminCard>

      {/* Table */}
      <AdminCard className="overflow-hidden">
        <div className="hidden md:grid grid-cols-[minmax(180px,1.5fr)_minmax(140px,1fr)_90px_120px_140px_120px_80px_60px] gap-3 px-4 py-3 text-[10px] font-semibold uppercase tracking-widest text-white/40 border-b border-white/[0.06]">
          <div>Cliente</div><div>Azienda</div><div>Piano</div><div>Stato</div><div>PM</div><div>Consegna</div><div>Fatt.</div><div></div>
        </div>
        {isLoading && <div className="p-8 text-center text-sm text-white/40">Caricamento…</div>}
        {!isLoading && filtered.length === 0 && (
          <EmptyState icon={Users} title="Nessun cliente trovato" description="Modifica i filtri o aggiungi il primo cliente." />
        )}
        {filtered.map((p) => {
          const prof = data?.profiles[p.owner_id];
          const invs = data?.invoicesByProject[p.id] ?? [];
          const tickets = data?.ticketsByProject[p.id] ?? [];
          const openTickets = tickets.filter((t) => !["completed", "closed"].includes(t.status)).length;
          return (
            <Link key={p.id} to="/admin/clients/$id" params={{ id: p.id }}
              className="grid md:grid-cols-[minmax(180px,1.5fr)_minmax(140px,1fr)_90px_120px_140px_120px_80px_60px] grid-cols-2 gap-3 px-4 py-3 text-sm border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02] items-center">
              <div className="min-w-0">
                <div className="font-medium truncate">{prof?.full_name ?? "Cliente"}</div>
                <div className="text-[11px] text-white/40 truncate">{p.name}</div>
              </div>
              <div className="text-white/70 text-xs truncate">{prof?.company ?? "—"}</div>
              <div><StatusPill status={p.plan ?? "—"} tone="violet" /></div>
              <div><StatusPill status={p.kanban_status ?? p.status ?? "—"} /></div>
              <div className="text-xs text-white/60 truncate">{p.project_manager ?? "—"}</div>
              <div className="text-xs text-white/60">{p.delivery_date ? format(new Date(p.delivery_date), "d MMM", { locale: it }) : "—"}</div>
              <div className="text-xs text-white/60 tabular-nums">
                <span className="text-emerald-300">{invs.filter((i) => i.status === "paid").length}</span>
                <span className="text-white/30">/</span>
                <span>{invs.length}</span>
                {openTickets > 0 && <span className="ml-2 text-amber-300">🎫{openTickets}</span>}
              </div>
              <div className="justify-self-end">
                {p.website_url && <ExternalLink className="h-3.5 w-3.5 text-white/30 hover:text-white" onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(p.website_url!, "_blank"); }} />}
              </div>
            </Link>
          );
        })}
      </AdminCard>
    </div>
  );
}

function FilterGroup({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <div className="flex items-center gap-1 rounded-lg bg-white/[0.02] p-0.5 border border-white/[0.06]">
      <span className="text-[10px] uppercase tracking-widest text-white/40 px-2">{label}</span>
      {options.map((o) => (
        <button key={o} onClick={() => onChange(o)}
          className={`px-2 py-1 rounded-md text-[11px] transition ${value === o ? "bg-white/10 text-white" : "text-white/50 hover:text-white"}`}>
          {o.replace("_", " ")}
        </button>
      ))}
    </div>
  );
}

function groupBy<T extends Record<string, unknown>>(arr: T[], key: string): Record<string, T[]> {
  return arr.reduce((acc, item) => {
    const k = String(item[key] ?? "");
    (acc[k] ??= []).push(item);
    return acc;
  }, {} as Record<string, T[]>);
}
